import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MaterialReactTable } from 'material-react-table';
import {
  Box,
  Button,
  createTheme,
  ThemeProvider,
  useTheme,
} from '@mui/material';
import AddVehicleColumnButton from './AddVehicleColumnButton';
import AddColumnToDepartmentPropertyButton from './AddColumnToDepartmentPropertyButton';
import Dropdown from '../../inputs/Dropdown';
import TabEditVehicleColumnContainer from './TabEditVehicleColumnContainer';
import ConfirmationDeleteModal from '../../components/ConfirmationDeleteModal';
import {
  deleteDepartmentProperty,
  updateDepartmentProperty,
  updateDepartmentPropertiesOrder,
} from '../../actions/apiDepartmentUpdates';

const TabEditDepartmentProperties = () => {
  const dispatch = useDispatch();
  const globalTheme = useTheme();

  const [modalOpen, setModalOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null); // 'department_property'
  const [deleteId, setDeleteId] = useState(null);
  const [deleteName, setDeleteName] = useState(null);
  const [tableKey, setTableKey] = useState(0);

  const vehicle_columns = useSelector((state) => state.settings_departments.vehicle_columns);
  const department_properties = useSelector((state) => state.settings_admin.edit_department_properties);
  const set_dealerships = useSelector((state) => state.settings_dealerships.related_installations);
  const edit_department = useSelector((state) => state.settings_admin.edit_department);
  const original_departments = useSelector((state) => state.settings_departments.original_departments);

  const tableTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: 'light',
          primary: {
            main: '#007bff', // Bootstrap primary color
          },
        },
        components: {
          MuiCheckbox: {
            styleOverrides: {
              root: {
                '&.Mui-checked': {
                  color: '#007bff',
                },
                '&:hover': {
                  backgroundColor: 'rgba(0, 123, 255, 0.04)',
                },
              },
            },
          },
        },
      }),
    [globalTheme],
  );

  const departmentPropertiesColumns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Property Name',
        size: 150,
        enableEditing: false,
      },
		{
        accessorKey: 'delete',
        header: '',
        size: 80,
        enableEditing: false,
        Cell: ({ row }) => (
          <Button
            variant="contained"
            color="error"
            size="small"
            className="btn btn-danger btn-sm"
            onClick={() => {
              setDeleteType('department_property');
              setDeleteId(row.original.id[0]);
              setDeleteName(row.original.name[0] || 'this item');
              setModalOpen(true);
            }}
          >
            Delete
          </Button>
        ),
      },
    ],
    [set_dealerships, dispatch],
  );

  const vehicleColumns = useMemo(
    () => [
      {
        accessorKey: 'addto',
        header: 'Add +',
        size: 120,
        Cell: ({ row }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <AddColumnToDepartmentPropertyButton vehicle_column={row} />
          </Box>
        ),
      },
      {
        accessorKey: 'name',
        header: 'Name',
        size: 120,
      },
      {
        accessorKey: 'input_name',
        header: 'Input Type',
        size: 120,
      },
	  {
			accessorKey: 'default_value',
			header: 'Default Value',
			size: 120,
		},
		{
			accessorKey: 'has_default_value',
			header: 'Has Default Value',
			size: 120,
			Cell: ({ cell }) => cell.getValue()?.[0]?.toString() || 'false',
		},
      {
        accessorKey: 'automove',
        header: 'Automove',
        size: 120,
        Cell: ({ cell }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <span>{cell.getValue()?.[0] ? 'Yes' : 'No'}</span>
          </Box>
        ),
      },
    ],
    [],
  );

  const departmentPropertiesData = useMemo(
    () =>
      department_properties
        ?.slice()
        ?.sort((a, b) => (a.order || 0) - (b.order || 0))
        ?.map((property) => ({
          id: [property.id],
          department_id: [property.department_id],
          vehicle_column_id: [property.vehicle_column_id],
          order: [property.order || 0],
          name: [property.vehicle_column.name],
          dealership: [property.dealership || ''],
        })) || [],
    [department_properties],
  );

  // Data for vehicle_columns table
  const vehicleColumnsData = useMemo(
    () =>
      vehicle_columns.map((vehicle_column) => ({
        name: [vehicle_column.name],
        input_name: [vehicle_column.input_name],
        automove: [vehicle_column.automove],
        org: [vehicle_column],
		default_value: [vehicle_column.default_value],
		has_default_value: [vehicle_column.has_default_value],
        vehicle_column_id: [vehicle_column.id],
      })),
    [vehicle_columns],
  );

  const selectDepartments = () => {
    const department = edit_department || {};
    const selected_departments = department.id ? [department.id] : [];

    return original_departments.map((option) => ({
      id: option.id,
      label: option.name,
      selected: selected_departments.includes(option.id),
    }));
  };

  const tableRef = useRef();
  const departmentPropertiesTableRef = useRef();

  const handleConfirmDelete = () => {
    if (deleteType === 'department_property') {
      dispatch(deleteDepartmentProperty(edit_department.id, deleteId, 'deleteDepartmentPropertyError'));
    }
    setModalOpen(false);
    setDeleteType(null);
    setDeleteId(null);
    setDeleteName(null);
    setTableKey((prev) => prev + 1);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setDeleteType(null);
    setDeleteId(null);
    setDeleteName(null);
  };

  const handleRowOrderChange = (draggingRow, hoveredRow) => {
    const newData = [...departmentPropertiesData];
    if (hoveredRow && draggingRow) {
      newData.splice(hoveredRow.index, 0, newData.splice(draggingRow.index, 1)[0]);
      const newOrder = newData.map((item) => item.id[0]);
      dispatch(updateDepartmentPropertiesOrder(edit_department.id, newOrder, 'updateDepartmentPropertiesOrderError'));
      setTableKey((prev) => prev + 1);
    }
  };

  return (
    <>
      <ConfirmationDeleteModal
        isOpen={modalOpen}
        onRequestClose={handleCloseModal}
        onConfirm={handleConfirmDelete}
        message={`Are you sure you want to delete ${deleteName || 'this item'} a department property?`}
      />
      <div className="container-fluid">
        <div className="col-md-12">
          <h5 className="text-success text-center text-capitalize">
            {edit_department?.name || 'No Department Selected'}
            <br />
            <small className="text-warning text-center">Contact support if you need any assistance.</small>
          </h5>
          <div className="row">
            {/* Left Table: Department Properties */}
            <div className="card col-md-5">
              <div className={edit_department?.name ? 'card-header bg-success text-white' : 'card-header bg-warning text-white'}>
                <div className="p-2 text-capitalize">
                  {edit_department?.name || 'Select Departments'}
                </div>
              </div>
              <div className="card-body">
                <div className="d-flex flex-column">
                  <div className="p-2">
                    <div className="col-12">
                      <small>
                        <i className="fa fa-info-circle text-info" />
                        {' '}
                        Drag and Drop rows to change order
                      </small>
                    </div>
                    <div className="p-2 list-group setscroll">
                      <ThemeProvider theme={tableTheme}>
                        <MaterialReactTable
                          tableRef={departmentPropertiesTableRef}
                          columns={departmentPropertiesColumns}
                          data={departmentPropertiesData}
                          editDisplayMode="cell"
                          enableEditing
                          enableRowOrdering
                          enableRowDragging
                          enableSorting={false}
                          enableColumnFilters
                          initialState={{
                            showColumnFilters: true,
                          }}
                          muiRowDragHandleProps={({ table }) => ({
                            onDragEnd: () => {
                              const { draggingRow, hoveredRow } = table.getState();
                              if (hoveredRow && draggingRow) {
                                handleRowOrderChange(draggingRow, hoveredRow);
                              }
                            },
                          })}
                          sx={{ minHeight: '300px' }}
                        />
                      </ThemeProvider>
                    </div>
                  </div>
                </div>
              </div>
              <div className="card-footer" />
            </div>

            {/* Right Table: Vehicle Columns */}
            <div className="card col-md-7">
              <div className="card-header bg-dark text-white">
                <div className="row">
                  <div className="col-6">
                    <AddVehicleColumnButton />
                  </div>
                  <div className="col-6 text-dark">
                    <Dropdown
                      titleHelper="Department"
                      title={edit_department?.name || 'Select Department'}
                      list={selectDepartments()}
                      update_type={'set_edit_department'}
                    />
                  </div>
                </div>
              </div>
              <div className="card-body">
                <div className="d-flex flex-column">
                  <div className="p-2 list-group setscroll">
                    <ThemeProvider theme={tableTheme}>
                      <MaterialReactTable
                        key={tableKey}
                        tableRef={tableRef}
                        columns={vehicleColumns}
                        data={vehicleColumnsData}
                        enableSorting={false}
						enablePagination={false}
                        enableColumnFilters
                        initialState={{ 
							showColumnFilters: true,
						}}
                        renderDetailPanel={({ row }) => (
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-around',
                              alignItems: 'center',
                            }}
                          >
                            <TabEditVehicleColumnContainer vehicle_column={row} />
                          </Box>
                        )}
                      />
                    </ThemeProvider>
                  </div>
                </div>
              </div>
              <div className="card-footer" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TabEditDepartmentProperties;